import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Mic } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { AskSuperAnalystModal } from "./AskSuperAnalystModal";
import { useAuth } from "@/hooks/use-auth";

const HIDDEN_PREFIXES = ["/admin", "/dashboard", "/login", "/reset-password", "/data/connect"];

/**
 * Globally floating "Ask SuperAnalyst" entry point.
 * Mounted once at the app root. Fixed bottom-right on every route except auth/admin.
 * Mobile: circular icon-only button. Desktop: pill with label.
 */
export const AskSuperAnalystFloating = () => {
  const [open, setOpen] = useState(false);
  const [autoStartVoice, setAutoStartVoice] = useState(false);
  const { pathname } = useLocation();
  const { isRealAdmin } = useAuth();
  // Scrolled down, the desktop pill shrinks to its mark so it stops covering tables and charts (2 Nov 2026,
  // his screenshot); hover or keyboard focus opens it again, and at the top of a page it stays full.
  const [small, setSmall] = useState(false);
  useEffect(() => {
    const on = () => setSmall(window.scrollY > 320);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [pathname]);

  if (HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }

  const openText = () => {
    setAutoStartVoice(false);
    setOpen(true);
  };
  const openVoice = () => {
    setAutoStartVoice(true);
    setOpen(true);
  };

  // Admins have the AdminToolbar pinned to bottom-right: the pill sits just above it, on the same side, so the
  // two never overlap and the pill never covers a page's left menu (it did at bottom-left, 2 Nov 2026).
  const positionClass = isRealAdmin
    ? "bottom-[72px] right-4 sm:bottom-[84px] sm:right-6"
    : "bottom-4 right-4 sm:bottom-6 sm:right-6";
  const fold = small ? "max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 group-focus-visible:max-w-[160px] group-focus-visible:opacity-100" : "max-w-[160px]";

  return (
    <>
      <div className={`fixed ${positionClass} z-40 flex items-center gap-2`}>
        {/* Mobile: round icon button + mic */}
        <button
          type="button"
          onClick={openVoice}
          aria-label="Ask SuperAnalyst with voice"
          className={`sm:hidden ${small ? "hidden" : "inline-flex"} items-center justify-center w-12 h-12 rounded-full bg-card border border-border/40 hover:border-accent/50 transition-colors`}
        >
          <Mic className="w-5 h-5 text-accent" />
        </button>
        <button
          type="button"
          onClick={openText}
          aria-label="Ask SuperAnalyst"
          className="sm:hidden inline-flex items-center justify-center w-12 h-12 rounded-full bg-card border border-border/40 hover:border-accent/50 transition-colors"
        >
          <LogoMark size={22} />
        </button>

        {/* Desktop: pill with embedded mic */}
        <button
          type="button"
          onClick={openText}
          aria-label="Ask SuperAnalyst"
          className={`hidden sm:inline-flex group items-center h-11 rounded-[10px] ${small ? "gap-0 pl-[11px] pr-[11px] hover:gap-3 hover:pl-3 hover:pr-1.5 focus-visible:gap-3 focus-visible:pl-3 focus-visible:pr-1.5" : "gap-3 pl-3 pr-1.5"} bg-card/95 backdrop-blur-md border border-[var(--sn-border-strong)] hover:border-primary transition-[border-color,gap,padding] duration-300`}
        >
          <LogoMark size={22} className="shrink-0" />
          <span className={`overflow-hidden whitespace-nowrap text-sm text-muted-foreground group-hover:text-foreground transition-all duration-300 ${fold}`}>
            Ask SuperAnalyst...
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              openVoice();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                openVoice();
              }
            }}
            aria-label="Ask SuperAnalyst with voice"
            title="Speak your question"
            className={`${small ? "hidden group-hover:inline-flex group-focus-visible:inline-flex" : "inline-flex"} items-center justify-center w-8 h-8 rounded-[7px] bg-background border border-[var(--sn-border-strong)] text-muted-foreground hover:text-accent hover:border-accent/50 transition-colors shrink-0`}
          >
            <Mic className="w-4 h-4" />
          </span>
          {/* SN orange for the one solid action (colour ruling, 26 Oct 2026) */}
          <span className={`${small ? "hidden group-hover:inline-flex group-focus-visible:inline-flex" : "inline-flex"} items-center justify-center w-8 h-8 rounded-[7px] bg-[#EE5A24] text-white group-hover:opacity-90 transition-opacity shrink-0`}>
            <ArrowRight className="w-4 h-4" />
          </span>
        </button>
      </div>

      <AskSuperAnalystModal
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setAutoStartVoice(false);
        }}
        autoStartVoice={autoStartVoice}
      />
    </>
  );
};
